import React from 'react';
import { motion } from 'framer-motion';
import { Gamepad2, Coins, Star, ChevronDown, Gift } from 'lucide-react';

const HeroSection = () => {
  const statusBadges = [
    { label: 'SEASON: LIVE', icon: Star, color: 'bg-success-green', textColor: 'text-dark-border' },
    { label: 'QUESTS: OPEN', icon: Gamepad2, color: 'bg-neon-pink', textColor: 'text-dark-border' },
    { label: 'PAYOUTS: ACTIVE', icon: Coins, color: 'bg-trophy-gold', textColor: 'text-dark-border' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 pb-12 px-4 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--neon-pink) / 0.3) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--neon-pink) / 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
        
        {/* Floating coins */}
        <motion.div
          className="absolute top-1/4 left-[10%]"
          animate={{ y: [0, -20, 0], rotate: [0, 10, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          <Coins className="w-12 h-12 text-trophy-gold opacity-40" />
        </motion.div>
        <motion.div
          className="absolute top-1/3 right-[15%]"
          animate={{ y: [0, -15, 0], rotate: [0, -10, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
        >
          <Gift className="w-16 h-16 text-trophy-gold opacity-30" />
        </motion.div>
        <motion.div
          className="absolute bottom-1/3 left-[20%]"
          animate={{ y: [0, -25, 0] }}
          transition={{ duration: 5, repeat: Infinity, delay: 1 }}
        >
          <Star className="w-10 h-10 text-neon-pink opacity-30" />
        </motion.div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto text-center">
        {/* Status Badges */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap justify-center gap-3 mb-8"
        >
          {statusBadges.map((badge, index) => (
            <motion.div
              key={badge.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 + 0.3 }}
              className={`flex items-center gap-2 px-4 py-2 ${badge.color} ${badge.textColor} font-pixel text-[8px] sm:text-[10px] border-2 border-dark-border shadow-arcade-sm`}
            >
              <badge.icon className="w-3 h-3 sm:w-4 sm:h-4" />
              {badge.label}
            </motion.div>
          ))}
        </motion.div>

        {/* Main Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-6"
        >
          <h1 className="font-pixel text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-foreground leading-relaxed">
            REAL-WORLD <span className="text-trophy-gold">SIDE QUESTS</span> FOR AI
          </h1>
        </motion.div>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="font-terminal text-xl sm:text-2xl md:text-3xl text-lavender max-w-3xl mx-auto mb-10 leading-relaxed"
        >
          Complete side quests. Upload real-life video data.
          <span className="text-trophy-gold"> Get paid.</span> Help train AI & robots.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12"
        >
          <a
            href="#submit"
            className="btn-arcade w-full sm:w-auto font-pixel text-[10px] sm:text-xs px-6 sm:px-8 py-4 bg-neon-pink text-dark-border hover:bg-neon-pink/90 animate-pulse-glow"
          >
            <Gamepad2 className="inline-block w-4 h-4 mr-2" />
            BECOME A SIDE QUESTER
          </a>
          <a
            href="#side-quests"
            className="btn-arcade w-full sm:w-auto font-pixel text-[10px] sm:text-xs px-6 sm:px-8 py-4 bg-panel-purple text-foreground border-2 border-neon-pink hover:bg-neon-pink/20"
          >
            <Star className="inline-block w-4 h-4 mr-2" />
            VIEW SIDE QUESTS
          </a>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ 
            opacity: { delay: 1.2 },
            y: { duration: 1.5, repeat: Infinity }
          }}
          className="text-center"
        >
          <a href="#what-it-is" className="inline-flex flex-col items-center text-lavender hover:text-neon-pink transition-colors">
            <span className="font-terminal text-lg mb-2">LEARN MORE</span>
            <ChevronDown className="w-6 h-6" />
          </a>
        </motion.div>
      </div>

      {/* Bottom decorative border */}
      <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-neon-pink via-trophy-gold to-success-green opacity-80" />
    </section>
  );
};

export default HeroSection;
